/* ============================================================
   search-boot.js — fetches search.js on first contact with the header.

   Loaded on every template. Deliberately tiny: it exists so that search.js,
   which is not, does not have to be.

   WHY THERE IS A SEPARATE FILE FOR FIFTEEN LINES.

   assets/search.js used to be loaded unconditionally by layout/theme.liquid,
   and at ~4.0KB brotli it was the single largest thing on the worst-case page.
   Phase 13 made it on-demand — one of the two real levers for JS
   headroom — and accumulated bug-fix work had put the
   collection/search teaser page 234 bytes over the per-page gate as it was
   measured then — a whole-theme figure, on a basis since replaced.

   The loader belonged in nmp.js by every argument except the one that
   decided it: under the raw per-file cap in force at the time, this code plus
   the comment CLAUDE.md requires did not fit in what nmp.js had left. Phase 13
   squeezed it in and the file measured over; trimming commentary to close that
   gap is the lever CLAUDE.md explicitly forbids, so it moved here.

   ⚠ THAT ARGUMENT NO LONGER HOLDS, and the file stays anyway (2026-09-09). The
   per-file cap stopped charging for comments, so nmp.js has room for this now.
   What it does not have is a reason to take it: keeping the loader separate is
   what lets search.js be fetched on contact instead of on load, and that is
   the deferral the per-page budget actually buys. The cap forced the split;
   the deferral is why it was worth keeping.

   WHY IT IS SAFE TO DEFER SEARCH AT ALL.

   The overlay does not depend on search.js to work. <nmp-search-overlay> is an
   <nmp-overlay>, so the panel, its focus trap, Escape and scrim close all come
   from nmp.js, and the field inside it is a real GET form pointing at /search
   — typing and pressing enter returns results with no script present at all.
   search.js only puts some of those results on screen sooner.

   The old layout comment priced the objection as "costs the first keystroke in
   the search field". That assumed loading on the keystroke. Loading on first
   pointerdown or focus anywhere near the header starts the fetch before the
   panel has finished opening, and losing that race degrades to the native form
   rather than to a broken control.

   Last updated: 2026-09-07
   ============================================================ */
(function () {
  'use strict';

  /* The URL rides on <nmp-search data-src>, set by sections/search-overlay.liquid:
     a static asset cannot call asset_url itself. Queried per event rather than
     cached at module scope so a theme-editor section reload, which replaces the
     element, cannot leave this holding a detached node. */
  function boot(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    /* Any overlay trigger, not only search: reaching for the bag or the nav
       means the header is in use, and the fetch is free at that point. */
    if (!target.closest('[data-nmp-open], nmp-search')) return;

    var host = document.querySelector('nmp-search[data-src]');
    if (!host) return;

    document.removeEventListener('pointerdown', boot);
    document.removeEventListener('focusin', boot);

    /* Inserted scripts are async by default, which is what we want: nothing
       waits on this one, and search.js guards its single optional dependency
       on window.NmpAdoptSectionStyles. */
    var script = document.createElement('script');
    script.src = host.getAttribute('data-src');
    /* A blocked or missing file used to leave both listeners removed with no
       second attempt for the life of the page (2026-09-07). Re-arm, so the
       next reach for the header tries again; the GET form was always the
       real path and never waited on this. */
    script.onerror = function () {
      script.remove();
      document.addEventListener('pointerdown', boot, { passive: true });
      document.addEventListener('focusin', boot);
    };
    document.head.appendChild(script);
  }

  document.addEventListener('pointerdown', boot, { passive: true });
  document.addEventListener('focusin', boot);
})();
